import dayjs from "dayjs";
import {ClubBookingApiClient} from "../../common/api/ClubBookingApiClient";
import { CreatePointOfSaleTransactionRequest } from "../../model/pointofsale/CreatePointOfSaleTransactionRequest";
import { PointOfSaleItem } from "../../model/pointofsale/PointOfSaleItem";
import { PointOfSaleTransaction } from "../../model/pointofsale/PointOfSaleTransaction";
import { UpdatePaymentTransactionRequest } from "../../model/pointofsale/UpdatePaymentTransactionRequest";

export class PointOfSaleService extends ClubBookingApiClient {
    async getPosItems(): Promise<PointOfSaleItem[]> {
        // Stats window: the last month up to today.
        const from = dayjs().subtract(1, 'month').format('YYYY-MM-DD');
        const to = dayjs().format('YYYY-MM-DD');
        // showError because this one renders a TILL. Without it the base client resolves a
        // failed load to undefined and the terminal shows an empty item list — indistinguishable
        // from a club that sells nothing, with the operator left tapping a screen that will never
        // fill. The surrounding try/catch this replaces caught nothing: `this.get` was returned,
        // not awaited, so a rejection could never reach it.
        return this.get<PointOfSaleItem[]>(
            `/pos/items/withstats?from=${from}&to=${to}`,
            undefined,
            { showError: true },
        );
    }

    async getPosItem(name: string): Promise<PointOfSaleItem> {
        return this.get<PointOfSaleItem>(`/pos/item/${name}`);
    }

    async makePayment(request: CreatePointOfSaleTransactionRequest): Promise<PointOfSaleTransaction> {
        try {
            return this.post('/pos/transaction', request);
        } catch (error) {
            console.error('Error making payment:', error);
            throw error;
        }
    }

    async getPointOfSaleTransactions(membershipId: string): Promise<PointOfSaleTransaction[]> {
        return await this.get<PointOfSaleTransaction[]>(`/pos/transactions/${membershipId}`);
    }

    async getTransactionsForBooking(bookingId: number): Promise<PointOfSaleTransaction[]> {
        try {
            return this.get<PointOfSaleTransaction[]>(`/pos/transactions/booking/${bookingId}`);
        } catch (error) {
            console.error('Error getting point of sale transaction for booking:', error);
            throw error;
        }
    }

    async getAllPointOfSaleTransactions(): Promise<PointOfSaleTransaction[]> {
        try {
            return this.get<PointOfSaleTransaction[]>(`/pos/transactions/client`);
        } catch (error) {
            console.error('Error getting point of sale transactions by client:', error);
            throw error;
        }
    }

    async updateTransaction(request: UpdatePaymentTransactionRequest): Promise<PointOfSaleTransaction> {
        try {
            return this.put('/pos/transaction/update', request);
        } catch (error) {
            console.error('Error updating point of sale transaction:', error);
            throw error;
        }
    }

    async saveItem(item: PointOfSaleItem): Promise<PointOfSaleItem> {
        try {
            return this.put('/pos/item', item);
        } catch (error) {
            console.error('Error saving point of sale item:', error);
            throw error;
        }
    }

    async getItemTypes(): Promise<string[]> {
        try {
            return this.get<string[]>('/pos/item/types');
        } catch (error) {
            console.error('Error fetching item types:', error);
            throw error;
        }
    }

    async getPointOfSaleTransactionsByClient(from?: string, to?: string): Promise<PointOfSaleTransaction[]> {
        const params: Record<string, string> = {};
        
        if (from) {
            params.from = from; // Already in YYYY-MM-DD format
        }
        if (to) {
            params.to = to; // Already in YYYY-MM-DD format
        }

        console.log('Fetching point of sale transactions by client', params);
        const data = await this.get<PointOfSaleTransaction[]>('/pos/transactions', params);
        
        return data;
    }

}